import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { FIELD_CHROME, type FieldVariant } from '@/lib/field-chrome';
import { useFieldVariant } from '@/components/ui/field-variant';

const textareaVariants = cva(
  'flex w-full text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 dark:text-neutral-100 dark:placeholder:text-neutral-400',
  {
    variants: {
      variant: {
        solid: `${FIELD_CHROME.solid.base} ${FIELD_CHROME.solid.focusVisible}`,
        glass: `${FIELD_CHROME.glass.base} ${FIELD_CHROME.glass.focusVisible}`,
      },
      size: {
        sm: 'min-h-[80px] rounded-lg px-3 py-2 text-sm',
        md: 'min-h-[96px] rounded-xl px-4 py-3 text-sm',
        lg: 'min-h-[120px] rounded-2xl px-4 py-3 text-base',
      },
    },
    defaultVariants: {
      variant: 'solid',
      size: 'md',
    },
  }
);

type TextareaProps = React.ComponentProps<'textarea'> &
  Omit<VariantProps<typeof textareaVariants>, 'variant'> & {
    /**
     * Visual variant. Defaults to the nearest `FieldVariantProvider`, or
     * `'solid'` if none is present.
     */
    variant?: FieldVariant;
    /**
     * When `true` and `maxLength` is set, renders a `current/max` character
     * counter in the bottom-right of the textarea. Requires the textarea to
     * be controlled (`value` prop) to read the current length.
     */
    showCounter?: boolean;
  };

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, size, variant, showCounter, maxLength, value, ...props }, ref) => {
    const resolvedVariant = useFieldVariant(variant);

    const textareaEl = (
      <textarea
        className={cn(textareaVariants({ variant: resolvedVariant, size }), className)}
        maxLength={maxLength}
        value={value}
        ref={ref}
        {...props}
      />
    );

    if (!showCounter || !maxLength) return textareaEl;

    const currentLength = typeof value === 'string' ? value.length : 0;
    return (
      <div className="relative w-full">
        {textareaEl}
        <div className="pointer-events-none absolute bottom-2 right-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
          {currentLength}/{maxLength}
        </div>
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea, textareaVariants };
